import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CTAButton from '../components/CTAButton';
import { blogPosts } from '../data/blogPosts';
import { MEDIA } from '../config/media';
import SEO from '../components/SEO';

const { FiSearch, FiCalendar, FiClock, FiX, FiArrowRight, FiLayers, FiShield, FiTarget, FiZap } = FiIcons;

function Blog() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const popularTopics = ['Trades', 'CRM', 'Automation', 'Systems', 'NDIS', 'Dashboards', 'AI'];
  
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesSearch = searchTerm === '' || post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) || post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesTopic = !selectedTopic || post.tags.some(tag => tag.toLowerCase() === selectedTopic.toLowerCase());
      return matchesSearch && matchesTopic;
    });
  }, [searchTerm, selectedTopic]);

  const blogImages = Object.values(MEDIA.blogThumbs);
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTopic(null);
  };

  return ( 
    <main className="overflow-x-hidden text-left"> 
      <SEO 
        title="Business Automation Insights & Web Design Articles | BPA Solutions" 
        description="Practical advice, real examples, and automation strategies specifically for Australian small businesses. Read our latest insights." 
      />

      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16 sm:py-20 lg:py-24"> 
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} > 
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight tracking-tight"> 
              Business Automation Insights & Web Design Articles 
            </h1> 
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-4 max-w-2xl mx-auto leading-relaxed"> 
              Practical advice, real examples, and automation strategies specifically for Australian small businesses. Learn how to reclaim your time and scale efficiently. 
            </p>
            <p className="text-xs sm:text-sm text-slate-500 mb-10">
              Content library updated <time dateTime="2026-09-16">16 September 2026</time>
            </p>
            <div className="relative max-w-md mx-auto"> 
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" /> 
              <input type="text" placeholder="Search articles..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-4 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm text-sm sm:text-base" /> 
            </div> 
          </motion.div> 
        </div> 
      </section> 

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20"> 
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-16"> 
          <div className="lg:col-span-3"> 
            {filteredPosts.length === 0 ? ( 
              <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200"> 
                <p className="text-slate-600 text-lg mb-4">No articles found matching your criteria.</p> 
                <button onClick={clearFilters} className="text-blue-600 font-bold hover:underline">Show all posts</button> 
              </div> 
            ) : ( 
              <div className="grid gap-8 sm:gap-10"> 
                {filteredPosts.map((post) => {
                  const originalIndex = blogPosts.findIndex(p => p.slug === post.slug);
                  return ( 
                    <article key={post.slug} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 group" > 
                      <div className="flex flex-col md:flex-row"> 
                        <div className="md:w-1/3 relative overflow-hidden h-48 sm:h-auto"> 
                          <img src={blogImages[originalIndex % blogImages.length]?.src} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" width="400" height="250" /> 
                        </div> 
                        <div className="p-6 sm:p-8 md:w-2/3 flex flex-col text-left"> 
                          <div className="flex flex-wrap items-center text-xs sm:text-sm text-slate-500 mb-4 font-medium gap-x-4"> 
                            <div className="flex items-center"> 
                              <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2 text-blue-500" /> 
                              <time dateTime={post.date}>{post.date}</time> 
                            </div> 
                            <div className="flex items-center"> 
                              <SafeIcon icon={FiClock} className="h-4 w-4 mr-2 text-blue-500" /> 
                              <span>{post.readingTime}</span> 
                            </div> 
                          </div> 
                          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 leading-tight group-hover:text-blue-600 transition-colors tracking-tight"> 
                            <Link to={`/blog/${post.slug}`}> {post.title} </Link> 
                          </h2> 
                          <p className="text-sm sm:text-base text-slate-600 mb-6 line-clamp-2 sm:line-clamp-3 leading-relaxed"> {post.excerpt} </p> 
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-auto gap-4"> 
                            <div className="flex flex-wrap gap-2"> 
                              {post.tags.slice(0, 2).map((tag, tagIndex) => ( 
                                <span key={tagIndex} className="text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-3 py-1 rounded-full border border-slate-200"> {tag} </span> 
                              ))} 
                            </div> 
                            <Link to={`/blog/${post.slug}`} className="text-blue-600 hover:text-blue-700 font-bold text-sm sm:text-base flex items-center group-hover:translate-x-1 transition-transform" > 
                              Read More <span className="ml-1">→</span> 
                            </Link> 
                          </div> 
                        </div> 
                      </div> 
                    </article> 
                  );
                })} 
              </div> 
            )} 
          </div> 
          <aside className="space-y-10 lg:sticky lg:top-28 lg:h-fit text-left"> 
            <div className="bg-blue-600 text-white rounded-2xl p-8 shadow-xl"> 
              <h3 className="text-lg font-bold mb-4 tracking-tight">Need Custom Advice?</h3> 
              <p className="text-blue-100 text-sm sm:text-base mb-6 leading-relaxed"> Let's discuss your specific business challenges in a free 20-minute consultation. We specialize in <strong>business automation + website design</strong> for Australian SMBs. </p> 
              <CTAButton size="sm" className="w-full bg-white text-blue-600 hover:bg-blue-50"> Book a Free Consult </CTAButton> 
            </div> 
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200"> 
              <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center tracking-tight"> 
                <span className="w-1.5 h-6 bg-blue-600 rounded-full mr-3"></span> Popular Topics 
              </h3> 
              <div className="flex flex-wrap gap-2.5"> 
                {popularTopics.map((topic) => ( 
                  <button key={topic} onClick={() => setSelectedTopic(topic === selectedTopic ? null : topic)} className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all border ${selectedTopic === topic ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'}`} > {topic} </button> 
                ))} 
              </div> 
            </div> 
          </aside> 
        </div> 
      </div>

      <section className="py-16 sm:py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Why We Share Automation Insights</h2>
          <p className="text-slate-600 mb-6 leading-relaxed">
            At BPA Solutions, we believe that an informed business owner is a successful one. The Australian market is evolving rapidly, and staying ahead of the technology curve is no longer optional - it's a requirement for survival. Our blog is designed to demystify complex concepts like <strong>AI agents for small businesses</strong> and <strong>workflow automation for trades Australia</strong>.
          </p>
          <p className="text-slate-600 mb-10 leading-relaxed">
            We focus on practical, actionable advice that you can implement today to see immediate results in your operational efficiency. From choosing the right CRM to understanding the latest in website conversion optimization, we cover the topics that matter most to local Australian service providers.
          </p>
          <CTAButton>Subscribe to Insights</CTAButton>
        </div>
      </section>

      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-10">Explore Our Solutions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/services" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiLayers} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Our Services</h3>
              <p className="text-sm text-slate-500 mb-4">See how we automate small business admin.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Browse All <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/about" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">About Our Team</h3>
              <p className="text-sm text-slate-500 mb-4">Learn about our Sydney-based consultancy.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">About Us <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/resources" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Free Resources</h3>
              <p className="text-sm text-slate-500 mb-4">Downloadable templates and checklists.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">View Tools <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/contact" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Get Started</h3>
              <p className="text-sm text-slate-500 mb-4">Book your free automation strategy call.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Contact Us <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>
    </main> 
  );
}

export default Blog;